import { useAuth } from '../hooks/useAuth';
import { useState, useEffect } from 'react';
import { getApiUrl } from '../hooks/useAuth';
import { formatDateToDDMMYYYY } from '../components/FormatedDate';
import { ToastContainer, toast } from 'react-toastify';

export default function Users() {
     const { user } = useAuth();
     const [showAddUser, setShowAddUser] = useState(false);
     const [users, setUsers] = useState([]);
     const [loading, setLoading] = useState(false);
     const [fetchError, setFetchError] = useState('');
     const [saving, setSaving] = useState(false);
     const [formError, setFormError] = useState('');
     const [roles, setRoles] = useState([]);
     const [form, setForm] = useState({
          emp_code: '',
          name: '',
          role_id: '',
          branch_id: '',
          status: 'A'
     });
     const token = sessionStorage.getItem('authToken');
     const BASE_URL = getApiUrl('').replace(/\/$/, '');
     const [editUserId, setEditUserId] = useState(null);

     //fetch roles
     const fetchRoles = async () => {
          if (!BASE_URL) return;
          try {
               const url = `${BASE_URL}/getRoles`;
               const response = await fetch(url, {
                    headers: {
                         'Authorization': `Bearer ${token}`,
                         'Accept': 'application/json'
                    },
                    method: 'GET',
               });
               if (!response.ok) throw new Error(`Failed to fetch roles (${response.status})`);
               const data = await response.json();
               setRoles(Array.isArray(data) ? data : (data?.data ?? []));
          } catch (error) {
               console.error('Error fetching roles:', error);
               // setFetchError(error.message || 'Failed to fetch roles');
          }
     };

     useEffect(() => {
          fetchRoles();
     }, [BASE_URL, token]);

     // Fetch users on component mount
     const fetchUsers = async () => {
          setLoading(true);
          setFetchError('');
          try {
               const url = `${BASE_URL}/getUsers`;
               const response = await fetch(url, {
                    headers: {
                         'Authorization': `Bearer ${token}`,
                         'Accept': 'application/json'
                    },
                    method: 'GET',
               });
               if (!response.ok) throw new Error(`Failed to fetch users (${response.status})`);
               const data = await response.json();
               setUsers(Array.isArray(data) ? data : (data?.data ?? []));
          } catch (error) {
               setFetchError(error.message || 'Failed to fetch users');
          } finally {
               setLoading(false);
          }
     };

     useEffect(() => {
          if (!BASE_URL) return;
          fetchUsers();
     }, [BASE_URL, token]);

     const getRoleBadgeClass = (roleId) => {
          const role = roles.find(r => String(r.role_id) === String(roleId));
          if (!role) return 'badge-info';
          switch (role.role_name) {
               case 'SUPER_ADMIN': return 'badge-danger';
               case 'ADMIN': return 'badge-warning';
               case 'QUALITY_ADMIN': return 'badge-success';
               case 'TECHNICIAN': return 'badge-primary';
               default: return 'badge-info';
          }
     };

     // Map for displaying role names in table
     const ROLE_MAP = roles.reduce((acc, r) => {
          acc[r.role_id] = r.description || r.role_name || r.role_id;
          return acc;
     }, {});

     const getStatusBadgeClass = (status) => (status === 'Active' ? 'badge-success' : 'badge-secondary');

     const resetForm = () => setForm({ emp_code: '', name: '', role_id: '', branch_id: '', status: 'A' });

     const validateForm = () => {
          if (!form.emp_code.trim()) return 'Employee code is required';
          if (!form.name.trim()) return 'Name is required';
          if (!form.role_id) return 'Role is required';
          if (!form.status) return 'Status is required';
          return '';
     };

     const handleSave = async () => {
          setFormError('');
          const validationMsg = validateForm();
          if (validationMsg) {
               setFormError(validationMsg);
               return;
          }
          try {
               setSaving(true);
               const body = new URLSearchParams();
               body.append('emp_code', form.emp_code.trim());
               body.append('name', form.name.trim());
               body.append('role_id', form.role_id);
               if (form.branch_id) body.append('branch_id', form.branch_id);
               body.append('status', form.status);

               let url = getApiUrl('/addUser');
               if (editUserId) {
                    url = getApiUrl(`/updateUser/${editUserId}`);
                    body.append('user_id', editUserId);
               }

               const res = await fetch(url, {
                    method: 'POST',
                    headers: {
                         Accept: 'application/json',
                         ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body,
               });
               const isJson = (res.headers.get('content-type') || '').includes('application/json');
               const data = isJson ? await res.json().catch(() => ({})) : {};
               if (!res.ok || data?.status !== true) {
                    throw new Error(data?.message || `Failed to ${editUserId ? 'update' : 'create'} user (${res.status})`);
               }
               setShowAddUser(false);
               resetForm();
               setEditUserId(null); // <-- Reset edit mode
               // Re-fetch users...
               fetchUsers();
               // Show success toast
               toast.success(`User ${editUserId ? 'updated' : 'created'} successfully`);
          } catch (e) {
               setFormError(e.message || `Failed to ${editUserId ? 'update' : 'create'} user`);
          } finally {
               setSaving(false);
          }
     };

     const handleEditUser = (user) => {
          setForm({
               emp_code: user.emp_code || '',
               name: user.name || '',
               role_id: user.role_id || '',
               branch_id: user.branch_id || '',
               status: user.status === 1 || user.status === '1' || user.status === 'A' ? 'A' : 'I'
          });
          setEditUserId(user.user_id || user.id);
          setShowAddUser(true);
     };

     return (
          <div>
               <ToastContainer position="top-right" />
               <div className="d-sm-flex align-items-center justify-content-between mb-4">
                    <h1 className="h3 mb-0 text-gray-800">User Management</h1>
                    <button
                         className="btn btn-primary"
                         onClick={() => setShowAddUser(true)}
                    >
                         <i className="fas fa-user-plus mr-2"></i>
                         Add New User
                    </button>
               </div>

               {fetchError && (
                    <div className="alert alert-danger">
                         <i className="fas fa-exclamation-triangle mr-2"></i>
                         {fetchError}
                    </div>
               )}

               {loading && (
                    <div className="alert alert-info">
                         <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                         Loading users...
                    </div>
               )}

               {/* Add User Modal */}
               {showAddUser && (
                    <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                         <div className="modal-dialog modal-lg">
                              <div className="modal-content">
                                   <div className="modal-header">
                                        <h5 className="modal-title">{editUserId ? 'Update User' : 'Add New User'}</h5>
                                        <button
                                             type="button"
                                             className="close"
                                             onClick={() => {
                                                  setShowAddUser(false);
                                                  resetForm();
                                                  setFormError('');
                                                  setEditUserId(null);
                                             }}
                                        >
                                             <span>&times;</span>
                                        </button>
                                   </div>
                                   <div className="modal-body">
                                        {formError && (
                                             <div className="alert alert-danger py-2">{formError}</div>
                                        )}
                                        <form onSubmit={e => e.preventDefault()}>
                                             <div className="row">
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Employee Code</label>
                                                            <input
                                                                 type="text"
                                                                 className="form-control"
                                                                 value={form.emp_code}
                                                                 onChange={e => setForm(f => ({ ...f, emp_code: e.target.value }))}
                                                                 required
                                                            />
                                                       </div>
                                                  </div>
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Full Name</label>
                                                            <input
                                                                 type="text"
                                                                 className="form-control"
                                                                 value={form.name}
                                                                 onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                                                 required
                                                            />
                                                       </div>
                                                  </div>
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Role</label>
                                                            <select
                                                                 className="form-control"
                                                                 value={form.role_id}
                                                                 onChange={e => setForm(f => ({ ...f, role_id: e.target.value }))}
                                                                 required
                                                            >
                                                                 <option value="">-- Select Role --</option>
                                                                 {roles.map(r => (
                                                                      <option key={r.role_id} value={r.role_id}>{r.role_name}</option>
                                                                 ))}
                                                            </select>
                                                       </div>
                                                  </div>
                                                  <div className="col-md-6">
                                                       <div className="form-group">
                                                            <label>Status</label>
                                                            <select
                                                                 className="form-control"
                                                                 value={form.status}
                                                                 onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                                                                 required
                                                            >
                                                                 <option value="A">Active</option>
                                                                 <option value="I">Inactive</option>
                                                            </select>
                                                       </div>
                                                  </div>
                                             </div>
                                        </form>
                                   </div>
                                   <div className="modal-footer">
                                        <button
                                             type="button"
                                             className="btn btn-secondary"
                                             onClick={() => {
                                                  setShowAddUser(false);
                                                  resetForm();
                                                  setFormError('');
                                             }}
                                             disabled={saving}
                                        >
                                             Cancel
                                        </button>
                                        <button
                                             type="button"
                                             className="btn btn-primary"
                                             onClick={handleSave}
                                             disabled={saving}
                                        >
                                             {saving ? (
                                                  <>
                                                       <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                                                       Saving...
                                                  </>
                                             ) : (
                                                  <>
                                                       <i className="fas fa-save mr-2" />
                                                       {editUserId ? 'Update User' : 'Add User'}
                                                  </>
                                             )}
                                        </button>
                                   </div>
                              </div>
                         </div>
                    </div>
               )}

               {/* Users Table */}
               <div className="card shadow mb-4">
                    <div className="card-header py-3">
                         <h6 className="m-0 font-weight-bold text-primary">All Users</h6>
                    </div>
                    <div className="card-body">
                         <div className="table-responsive">
                              <table className="table table-bordered" width="100%" cellSpacing="0">
                                   <thead>
                                        <tr>
                                             <th>User Code</th>
                                             <th>Name</th>
                                             <th>Role</th>
                                             <th>Status</th>
                                             <th>Created</th>
                                             <th> </th>
                                        </tr>
                                   </thead>
                                   <tbody>
                                        {(Array.isArray(users) ? users : []).map((u) => {
                                             const userId = u.user_id || u.id || u.emp_code;
                                             const code = u.emp_code || '';
                                             const name = u.name || '';
                                             const role = ROLE_MAP[u.role_id] || u.role_id || '';
                                             const status = u.status === 'A' ? 'Active' : 'Inactive';
                                             const created = u.created_at ? formatDateToDDMMYYYY(u.created_at) : '-';
                                             return (
                                                  <tr key={userId}>
                                                       <td>{code}</td>
                                                       <td>{name}</td>
                                                       <td>
                                                            <span className={`badge ${getRoleBadgeClass(u.role_id)}`}>{role}</span>
                                                       </td>

                                                       <td>
                                                            <span className={`badge ${getStatusBadgeClass(status)}`}>{status}</span>
                                                       </td>
                                                       <td> {created}</td>
                                                       <td> <button className="btn btn-sm btn-primary" onClick={() => handleEditUser(u)}>
                                                            <i className="fas fa-edit  " />
                                                       </button></td>
                                                  </tr>
                                             );
                                        })}
                                   </tbody>
                              </table>
                         </div>
                    </div>
               </div>
          </div>
     );
}